import React, { useEffect, useMemo, useState } from "react";
import useKeyboardNav from "../hooks/useKeyboardNav";
import { classNames } from "../workspace/utils";
import STEPS from "./steps";
import StepVisual from "./StepVisual";

const TOTAL = STEPS.length;

export default function TutorialModal({ isOpen, onClose, initialStep = 0 }) {
  const [index, setIndex] = useState(initialStep);

  useEffect(() => {
    if (isOpen) setIndex(initialStep || 0);
  }, [isOpen, initialStep]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const step = STEPS[index] || STEPS[0];
  const canPrev = index > 0;
  const canNext = index < TOTAL - 1;

  const goNext = () => setIndex((i) => Math.min(i + 1, TOTAL - 1));
  const goPrev = () => setIndex((i) => Math.max(i - 1, 0));

  const bindings = useMemo(() => {
    if (!isOpen) return {};
    const jump = (n) => () => {
      if (n >= 0 && n < TOTAL) setIndex(n);
    };
    return {
      arrowright: () => goNext(),
      arrowleft: () => goPrev(),
      enter: () => (canNext ? goNext() : onClose?.()),
      " ": () => (canNext ? goNext() : onClose?.()),
      backspace: () => goPrev(),
      escape: () => onClose?.(),
      "1": jump(0),
      "2": jump(1),
      "3": jump(2),
      "4": jump(3),
      "5": jump(4),
      "6": jump(5),
      "7": jump(6),
    };
  }, [isOpen, canNext, onClose]);

  useKeyboardNav(bindings, { enabled: isOpen });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-zinc-900/50 p-4 backdrop-blur-sm md:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
      onClick={onClose}
    >
      <div
        className="flex h-[560px] w-full max-w-[960px] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-panel"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-6 py-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
              {index + 1} of {TOTAL}
            </span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                Leads workspace tour
              </p>
              <h2 id="tutorial-title" className="text-sm font-semibold text-zinc-900">
                {step.title}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100"
            aria-label="Close tutorial"
          >
            Close · Esc
          </button>
        </header>

        <div className="grid flex-1 min-h-0 grid-cols-1 md:grid-cols-[420px,1fr]">
          <div className="hidden min-h-0 overflow-hidden border-r border-zinc-200 bg-zinc-50/80 p-6 md:block">
            <div className="flex h-full w-full items-center justify-center">
              <StepVisual variant={step.visual} highlight={step.highlight} />
            </div>
          </div>

          <div className="flex min-h-0 flex-col overflow-hidden">
            <div className="flex-1 min-h-0 overflow-hidden px-6 py-5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
                Step {index + 1}
              </p>
              <h3 className="mt-1 text-lg font-semibold text-zinc-900">{step.title}</h3>
              {step.subtitle ? (
                <p className="mt-1 text-sm text-zinc-600">{step.subtitle}</p>
              ) : null}
              <ul className="mt-4 space-y-2.5">
                {step.bullets.map((bullet, i) => (
                  <li key={i} className="flex gap-2.5 text-sm leading-5 text-zinc-700">
                    <span className="mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-indigo-200 bg-indigo-50 text-[10px] font-semibold text-indigo-700">
                      {i + 1}
                    </span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="shrink-0 border-t border-zinc-200 bg-zinc-50/60 px-6 py-3">
              <div className="flex items-center gap-1.5">
                {STEPS.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Go to step ${i + 1}: ${s.title}`}
                    aria-current={i === index ? "step" : undefined}
                    className={classNames(
                      "h-1.5 rounded-full transition-all",
                      i === index
                        ? "w-8 bg-indigo-500"
                        : i < index
                        ? "w-4 bg-indigo-300 hover:bg-indigo-400"
                        : "w-4 bg-zinc-300 hover:bg-zinc-400"
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-zinc-200 bg-white px-6 py-3">
          <div className="flex items-center gap-3 text-[11px] text-zinc-500">
            <Kbd>←</Kbd>
            <Kbd>→</Kbd>
            <span>navigate</span>
            <span className="text-zinc-300">·</span>
            <Kbd>1</Kbd>
            <span className="text-zinc-400">–</span>
            <Kbd>7</Kbd>
            <span>jump</span>
            <span className="text-zinc-300">·</span>
            <Kbd>Esc</Kbd>
            <span>close</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              disabled={!canPrev}
              className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ← Prev
            </button>
            {canNext ? (
              <button
                type="button"
                onClick={goNext}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Next →
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Got it ✓
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="inline-flex min-w-[20px] items-center justify-center rounded-md border border-zinc-300 bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-zinc-700 shadow-[0_1px_0_0_rgba(0,0,0,0.04)]">
      {children}
    </kbd>
  );
}
